import type { BlockPriceCards, PriceCard } from 'types/block-price-cards';
import React from 'react';
import { Button, Paragraph, Wrap } from '@leafwell/components';
import HeadingBody from './heading-body';

const PriceCard: React.FC<PriceCard> = ({
  name,
  price,
  badge,
  description,
  note,
}) => {
  return (
    <div className="bg-white rounded-xl p-6 flex flex-col gap-4 w-full md:flex-1 md:min-w-96">
      <Paragraph
        tagName="p"
        size="xsmall"
        className="uppercase tracking-wider text-gray-400 font-semibold"
      >
        {name}
      </Paragraph>

      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-5xl font-extrabold leading-[1.05] tracking-tight text-purple-500">
          {price}
          <span className="text-base font-semibold text-gray-500">/mo</span>
        </span>
        {badge && (
          <span className="bg-green text-black text-sm font-semibold px-3 py-1 rounded-full whitespace-nowrap">
            {badge}
          </span>
        )}
      </div>

      {description && (
        <Paragraph
          tagName="p"
          size="xsmall"
          className="text-gray-600"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}

      {note && (
        <div className="mt-auto border-t-2 border-zinc-400 bg-zinc-50 rounded-b-2xl">
          <Paragraph
            tagName="p"
            size="xsmall"
            className="text-gray-500 py-3 px-2"
            dangerouslySetInnerHTML={{ __html: note }}
          />
        </div>
      )}
    </div>
  );
};

const PriceCards: React.FC<BlockPriceCards> = ({
  sectionId,
  instanceId,
  title,
  description,
  priceCards,
  footerNote,
  button,
}) => {
  return (
    <>
      <Wrap id={sectionId} itemID={`ClinicsLocation-${instanceId}`}>
        {title || description ? (
          <div className="grid gap-y-6 justify-items-center">
            {title && (
              <HeadingBody
                level="2"
                tagName="h2"
                title={title}
                className="font-semibold text-center"
              />
            )}
            {description && (
              <Paragraph
                dangerouslySetInnerHTML={{ __html: description }}
                size="large"
                className="text-center lg:max-w-5xl"
              />
            )}
          </div>
        ) : null}
        <div className="grid gap-6 bg-lilac-50 rounded-2xl p-8">
          {priceCards?.length > 0 ? (
            <div className="flex flex-wrap gap-6 justify-center">
              {priceCards.map(priceCard => (
                <PriceCard key={priceCard.name} {...priceCard} />
              ))}
            </div>
          ) : null}
          {footerNote && (
            <Paragraph
              dangerouslySetInnerHTML={{ __html: footerNote }}
              size="xsmall"
              className="font-medium text-gray-500"
            />
          )}
          {button && (
            <Button
              href={button.url}
              label={button.title}
              target={button.target}
              variant="primary"
              rounded={true}
              className="w-fit h-fit"
            />
          )}
        </div>
      </Wrap>
    </>
  );
};

export default PriceCards;
